import { useHttpRequest } from '~/composables/useHttpRequest';
import { endpoints } from '~/network/endpoints.js';

export const useMessagingApi = () => {
    const { httpRequest } = useHttpRequest();

    // Use useCrudApi for standard CRUD operations - ALWAYS destructure
    const {
        createItem: createConversationItem,
        updateItem: updateConversationItem,
        getItem: getConversationItem,
        deleteItem: deleteConversationItem,
        formErrors: conversationFormErrors,
        hasFormErrors: conversationHasFormErrors,
        itemErrors: conversationItemErrors,
        hasItemErrors: conversationHasItemErrors,
        item: conversationItem
    } = useCrudApi('api/v2/conversations');

    const {
        createItem: createMessageItem,
        getItems: getMessageItems,
        formErrors: messageFormErrors,
        hasFormErrors: messageHasFormErrors,
        item: messageItem,
        items: messageItems
    } = useCrudApi('api/v2/messages');

    /**
     * Sync conversations and messages from server
     * @param {string|null} lastSyncedAt - ISO timestamp of last sync
     * @returns {Promise<{conversations: Array, messages: Array, syncedAt: string}>}
     */
    const syncMessages = async (lastSyncedAt = null) => {
        try {
            const endpoint = {
                path: '/api/v2/messages/sync',
                method: 'post',
                requiresAuth: true
            };

            const request = {
                lastSyncedAt: lastSyncedAt
            };

            const response = await httpRequest(endpoint, request);
            return response;
        } catch (error) {
            console.error('Error syncing messages:', error);
            throw error;
        }
    };

    /**
     * Mark a message as read (DEPRECATED - use markConversationAsRead instead)
     * @param {string} messageId - Message ID
     * @returns {Promise<Object>} - Updated message with read receipts
     */
    const markMessageAsRead = async (messageId) => {
        try {
            const endpoint = {
                path: `/api/v2/messages/${messageId}/read`,
                method: 'post',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, {});
            return response;
        } catch (error) {
            console.error('Error marking message as read:', error);
            throw error;
        }
    };

    /**
     * Mark a conversation as read
     * @param {string} conversationId - Conversation ID
     * @param {string|null} readUpToTimestamp - ISO timestamp of last message read (null = mark all as read)
     * @returns {Promise<Object>} - Updated conversation detail response
     */
    const markConversationAsRead = async (conversationId, readUpToTimestamp = null) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/read`,
                method: 'post',
                requiresAuth: true
            };

            // Always send the request body matching MarkConversationAsReadRequest
            const request = {
                readUpToTimestamp: readUpToTimestamp
            };

            const response = await httpRequest(endpoint, request);
            return response;
        } catch (error) {
            console.error('Error marking conversation as read:', error);
            throw error;
        }
    };

    /**
     * Create a new conversation
     * @param {Object} data - { conversationType, title, participantUserIds }
     * @returns {Promise<Object>} - Created conversation
     */
    // Straight through httpRequest, NOT useCrudApi (29 Sep 2026): useCrudApi
    // swallows a refusal and leaves conversationItem as whatever it held
    // before, so a start the server refused (a block, no shared club) could
    // return the PREVIOUS conversation and the caller opened it. httpRequest
    // throws on any non-2xx with the server's {global:[...]} body.
    const createConversation = (data) => httpRequest({
        path: '/api/v2/conversations',
        method: 'post',
        requiresAuth: true,
    }, data);

    /**
     * Create a conversation linked to an entity (ladder, club, etc.)
     * @param {Object} data - { conversationType: 2, ladderId?, clubId? }
     * @returns {Promise<Object>} - Created conversation
     */
    const createEntityConversation = async (data) => {
        try {
            await createConversationItem(data);

            // Check if there are form errors (in case createItem doesn't throw)
            if (conversationHasFormErrors.value) {
                throw conversationFormErrors.value;
            }

            return conversationItem.value;
        } catch (error) {
            // Re-throw to ensure error propagates
            throw error;
        }
    };

    /**
     * Update a conversation
     * @param {string} conversationId
     * @param {Object} data - { title }
     * @returns {Promise<Object>} - Updated conversation
     */
    const updateConversation = async (conversationId, data) => {
        await updateConversationItem(conversationId, data);
        return conversationItem.value;
    };

    /**
     * Get conversation details
     * @param {string} conversationId
     * @returns {Promise<Object>}
     */
    const getConversation = async (conversationId) => {
        await getConversationItem(conversationId);
        return conversationItem.value;
    };

    /**
     * Delete a conversation (soft delete)
     * @param {string} conversationId
     * @returns {Promise<void>}
     */
    const deleteConversation = async (conversationId) => {
        await deleteConversationItem(conversationId);
    };

    /**
     * Send a message
     * @param {Object} data - { conversationId, content, messageType }
     * @returns {Promise<Object>} - Created message
     */
    const sendMessage = async (data) => {
        await createMessageItem(data);
        // useCrudApi does not throw on a 4xx — it parks the body in formErrors and
        // leaves `item` as it was. Returning that made a REFUSED send look like a
        // successful one all the way down, and the only symptom the sender ever
        // got was an IndexedDB DataError thrown further along by the code trying
        // to swap in a message the server never created. So a refusal that says
        // "You can't message this person." reached nobody. Throw it, carrying the
        // server's own words in the shape apiMessage() reads.
        if (messageHasFormErrors.value || !messageItem.value?.id) {
            // The sentence itself, not a stand-in: it is shown twice, once as an
            // alert and once inline under the failed message.
            const error = new Error(
                messageFormErrors.value?.global?.[0] || 'The message was not sent.');
            error.global = messageFormErrors.value?.global;
            error.data = messageFormErrors.value;
            throw error;
        }
        return messageItem.value;
    };

    /**
     * Delete my own sent message, for everyone in the chat (24 Sep 2026).
     * Straight through httpRequest, NOT useCrudApi: useCrudApi swallows a
     * network failure and resolves as though the delete worked, which left a
     * placeholder on screen for a message the server still had. httpRequest
     * throws on a failed fetch and on any non-2xx (with the server's `global`
     * sentence), and the caller checks the reply is this message's tombstone.
     * @param {string} messageId
     * @returns {Promise<{id: string, conversationId: string, deletedAt: string, deletedBy: string}>}
     */
    const deleteMessage = (messageId) => httpRequest({
        path: `/api/v2/messages/${messageId}`,
        method: 'delete',
        requiresAuth: true,
    });

    /**
     * Get messages for a conversation
     * @param {number} page
     * @param {number} perPage
     * @param {Object} filters - { conversationId }
     * @returns {Promise<Array>}
     */
    const getMessages = async (page = 1, perPage = 50, filters = {}) => {
        await getMessageItems(page, perPage, filters);
        return messageItems.value;
    };

    /**
     * Add participant to conversation
     * @param {string} conversationId
     * @param {string} userId
     * @returns {Promise<Object>} - Updated conversation
     */
    const addParticipant = async (conversationId, userId) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/participants`,
                method: 'post',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, { userId });
            return response;
        } catch (error) {
            console.error('Error adding participant:', error);
            throw error;
        }
    };

    /**
     * Add multiple participants to conversation
     * @param {string} conversationId
     * @param {Array<string>} userIds
     * @returns {Promise<Object>} - Updated conversation
     */
    const addParticipants = async (conversationId, userIds) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/participants/bulk`,
                method: 'post',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, { userIds });
            return response;
        } catch (error) {
            console.error('Error adding participants:', error);
            throw error;
        }
    };

    /**
     * Remove participant from conversation
     * @param {string} conversationId
     * @param {string} userId
     * @returns {Promise<Object>} - Updated conversation
     */
    const removeParticipant = async (conversationId, userId) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/participants/${userId}`,
                method: 'delete',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, {});
            return response;
        } catch (error) {
            console.error('Error removing participant:', error);
            throw error;
        }
    };

    /**
     * Leave a conversation (removes current user)
     * @param {string} conversationId
     * @returns {Promise<Object>} - Updated conversation
     */
    const leaveConversation = async (conversationId) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/leave`,
                method: 'delete',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, {});
            return response;
        } catch (error) {
            console.error('Error leaving conversation:', error);
            throw error;
        }
    };

    /**
     * Set admin status for participant
     * @param {string} conversationId
     * @param {string} userId
     * @param {boolean} isAdmin
     * @returns {Promise<Object>} - Updated conversation
     */
    const setAdminStatus = async (conversationId, userId, isAdmin) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/participants/${userId}/admin`,
                method: 'put',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, { isAdmin });
            return response;
        } catch (error) {
            console.error('Error setting admin status:', error);
            throw error;
        }
    };

    /**
     * Get notification status for a conversation
     * @param {string} conversationId
     * @returns {Promise<Object>} - { notificationsEnabled: boolean }
     */
    const getNotificationsEnabled = async (conversationId) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/notifications`,
                method: 'get',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, {});
            return response;
        } catch (error) {
            console.error('Error getting notification status:', error);
            throw error;
        }
    };

    /**
     * Set notification status for a conversation
     * @param {string} conversationId
     * @param {boolean} notificationsEnabled
     * @returns {Promise<Object>} - Updated conversation
     */
    const setNotificationsEnabled = async (conversationId, notificationsEnabled) => {
        try {
            const endpoint = {
                path: `/api/v2/conversations/${conversationId}/notifications`,
                method: 'put',
                requiresAuth: true
            };

            const response = await httpRequest(endpoint, { notificationsEnabled });
            return response;
        } catch (error) {
            console.error('Error setting notification status:', error);
            throw error;
        }
    };

    /**
     * Upload an image to get an imageUploadId
     * @param {string} base64Data - Base64 encoded image data
     * @param {string} filename - Original filename
     * @returns {Promise<Object>} - Upload response with id, fileLocation, etc.
     */
    const uploadImage = async (base64Data, filename) => {
        try {
            const endpoint = {
                path: '/api/v2/uploads',
                method: 'post',
                requiresAuth: true
            };

            const request = {
                base64Data,
                uploadType: 1, // Image type
                filename
            };

            const response = await httpRequest(endpoint, request);
            return response;
        } catch (error) {
            console.error('Error uploading image:', error);

            // If error is a Response object that hasn't been parsed, try to parse it
            // This can happen if httpRequest failed to parse the JSON
            if (error instanceof Response && !error.bodyUsed) {
                try {
                    const errorData = await error.json();
                    throw errorData;
                } catch (parseError) {
                    // If parsing fails, create a generic error object
                    throw {
                        exception: `Upload failed: ${error.status} ${error.statusText}`,
                        global: ['An error occurred while uploading the image']
                    };
                }
            }

            // If body was already consumed, create error from status
            if (error instanceof Response && error.bodyUsed) {
                throw {
                    exception: `Upload failed: ${error.status} ${error.statusText}`,
                    global: ['An error occurred while uploading the image']
                };
            }

            // Otherwise, throw as-is (should be parsed error object)
            throw error;
        }
    };

    return {
        deleteMessage,
        // Sync & custom endpoints
        syncMessages,
        markMessageAsRead, // DEPRECATED
        markConversationAsRead,

        // Conversations
        createConversation,
        createEntityConversation,
        updateConversation,
        getConversation,
        deleteConversation,
        addParticipant,
        addParticipants,
        removeParticipant,
        leaveConversation,
        setAdminStatus,
        getNotificationsEnabled,
        setNotificationsEnabled,

        // Messages
        sendMessage,
        getMessages,

        // Uploads
        uploadImage
    };
};
